import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import TokenIcon from "@/components/TokenIcon";
import PageTransition from "@/components/PageTransition";
import WalletModal from "@/components/WalletModal";
import CountUp from "@/components/CountUp";
import {
  disconnectWallet,
  getWallet,
  getBalances,
  getPositions,
  getHistory,
  calcPending,
  fmtAmount,
  vNow,
} from "@/lib/wallet";
import { ALL_TOKENS } from "@/data/tokens";
import {
  Wallet,
  LogOut,
  ShieldCheck,
  Copy,
  Check,
  ArrowRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Sparkles,
  TrendingUp,
  Activity,
  Clock,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

const TX_META = {
  stake:   { label: "Stake",   color: "var(--success)", icon: ArrowDownToLine },
  unstake: { label: "Unstake", color: "var(--warning)", icon: ArrowUpFromLine },
  claim:   { label: "Claim",   color: "#7dd3fc",        icon: Sparkles },
};

export default function Profile() {
  const [wallet, setWallet] = useState(null);
  const [balances, setBalances] = useState({});
  const [positions, setPositions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [, rerender] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setWallet(getWallet());
      setBalances(getBalances());
      setPositions(getPositions());
    };
    refresh();
    const sync = () => refresh();
    window.addEventListener("stk-wallet-change", sync);
    window.addEventListener("storage", sync);
    const i = setInterval(() => rerender((x) => x + 1), 1500);
    return () => {
      window.removeEventListener("stk-wallet-change", sync);
      window.removeEventListener("storage", sync);
      clearInterval(i);
    };
  }, []);

  const onDisconnect = () => {
    disconnectWallet();
    setWallet(null);
    toast.success("Wallet disconnected");
  };

  const copyAddress = async () => {
    if (!wallet) return;
    await navigator.clipboard.writeText(wallet.address);
    setCopied(true);
    toast.success("Address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const totalStakedAssets = positions.length;
  const totalPending = positions.reduce((a, p) => a + calcPending(p), 0);
  const totalStakedValue = positions.reduce((a, p) => a + p.amount, 0);
  const balanceTokenCount = Object.values(balances).filter((v) => v > 0).length;
  const boostCount = positions.filter((p) => p.pool === "boost").length;

  // Lifetime earned = sum of all claims in history
  const history = getHistory();
  const lifetimeEarned = history.filter((h) => h.type === "claim").reduce((a, h) => a + (h.amount || 0), 0);
  const daysActive = wallet
    ? Math.max(1, Math.floor((Date.now() - wallet.connectedAt) / 86400000))
    : 0;

  return (
    <PageTransition>
      <Navbar />

      {/* DISCONNECTED VIEW */}
      {!wallet && (
        <DisconnectedView onConnect={() => setModalOpen(true)} />
      )}

      {/* CONNECTED VIEW */}
      {wallet && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-16 sm:pb-20">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "var(--text-3)" }}>▍ My Profile</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                Welcome back,<br />
                <span className="grad-sol-text">{wallet.address.slice(0, 6)}…{wallet.address.slice(-4)}</span>
              </h1>
            </div>
            <button
              onClick={onDisconnect}
              data-testid="disconnect-btn"
              className="px-4 h-10 inline-flex items-center gap-2 text-xs uppercase tracking-wider border"
              style={{ borderColor: "var(--error)", color: "var(--error)" }}
            >
              <LogOut size={13} /> Disconnect
            </button>
          </div>

          {/* Hero KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mb-10" style={{ background: "var(--border)" }} data-testid="profile-kpi-grid">
            <Kpi label="Lifetime Earned" valueNode={<CountUp end={lifetimeEarned} decimals={4} duration={1400} />} accent="var(--success)" hint="claimed rewards" />
            <Kpi label="Pending Rewards" valueNode={<CountUp end={totalPending} decimals={4} duration={1400} />} accent="#FFB339" hint="auto-compounding" />
            <Kpi label="Total Staked"    valueNode={<CountUp end={totalStakedValue} decimals={2} duration={1400} />} hint={`across ${totalStakedAssets} positions`} />
            <Kpi label="Days Active"     valueNode={<CountUp end={daysActive} decimals={0} duration={1200} />} hint={`since ${new Date(wallet.connectedAt).toLocaleDateString()}`} />
          </div>

          {/* Wallet card */}
          <div className="grid lg:grid-cols-3 gap-px mb-10" style={{ background: "var(--border)" }}>
            <div className="lg:col-span-2 p-6 sm:p-7 relative overflow-hidden" style={{ background: "var(--surface-1)" }} data-testid="connected-wallet-card">
              <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full opacity-20 pointer-events-none drift"
                   style={{ background: wallet.chain === "evm" ? "var(--evm-grad)" : "var(--sol-grad)" }} />
              <div className="relative flex items-center gap-2 mb-3">
                <Wallet size={13} style={{ color: "var(--text-3)" }} />
                <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--text-3)" }}>Connected via {wallet.name}</span>
                <span className="w-1.5 h-1.5 rounded-full pulse-dot ml-1" style={{ background: "var(--success)" }} />
              </div>
              <div className="relative font-mono text-sm sm:text-base break-all leading-relaxed">{wallet.address}</div>
              <div className="relative mt-4 flex gap-2 flex-wrap">
                <button
                  onClick={copyAddress}
                  data-testid="copy-address-btn"
                  className="px-3 h-9 border inline-flex items-center gap-2 text-xs uppercase tracking-wider lift"
                  style={{ borderColor: "var(--border-strong)" }}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <button
                  onClick={() => window.location.href = "portfolio/index.html"}
                  data-testid="quick-portfolio-btn"
                  className="px-3 h-9 border inline-flex items-center gap-2 text-xs uppercase tracking-wider lift"
                  style={{ borderColor: "var(--border-strong)" }}
                >
                  Portfolio <ArrowRight size={11} />
                </button>
              </div>
            </div>

            {/* Right: network + boost badge */}
            <div className="p-6 sm:p-7 flex flex-col justify-between" style={{ background: "var(--surface-1)" }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Network</div>
                <div className="font-display font-extrabold text-3xl sm:text-4xl mt-1 tracking-tighter" style={{ color: wallet.chain === "evm" ? "var(--evm)" : "var(--sol)" }}>
                  {wallet.chain.toUpperCase()}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                  {wallet.chain === "evm" ? "Ethereum + L2s" : "Solana mainnet-beta"}
                </div>
              </div>
              {boostCount > 0 && (
                <div className="mt-5 flex items-center gap-2 px-3 py-2 grad-boost-bg" style={{ color: "#fff" }}>
                  <Zap size={12} className="flicker" />
                  <span className="text-[10px] uppercase tracking-wider font-bold">
                    {boostCount} active 24h boost {boostCount === 1 ? "pool" : "pools"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quick links */}
          <div className="flex flex-wrap gap-3 mb-12">
            <Link to="/markets/evm" data-testid="quick-evm-link" className="px-5 h-11 inline-flex items-center gap-2 font-bold tracking-wide grad-evm-bg btn-glow-evm" style={{ color: "#fff" }}>
              EVM MARKET <ArrowRight size={14} />
            </Link>
            <Link to="/markets/solana" data-testid="quick-sol-link" className="px-5 h-11 inline-flex items-center gap-2 font-bold tracking-wide grad-sol-bg btn-glow-sol" style={{ color: "#fff" }}>
              SOLANA MARKET <ArrowRight size={14} />
            </Link>
          </div>

          {/* Active positions */}
          {positions.length > 0 && (
            <div className="mb-12" data-testid="profile-positions">
              <SectionHeader icon={Activity} label="Active positions" count={positions.length} />
              <div className="border" style={{ borderColor: "var(--border)" }}>
                {positions.map((p, idx) => {
                  const meta = ALL_TOKENS.find((t) => t.symbol === p.token && t.chain === p.chain);
                  const pending = calcPending(p);
                  const days = Math.floor((vNow() - p.startedAt) / 86400000);
                  const isBoost = p.pool === "boost";
                  return (
                    <div
                      key={p.id}
                      data-testid={`position-row-${p.id}`}
                      className="grid grid-cols-12 px-4 sm:px-6 py-4 items-center text-sm gap-3"
                      style={{ borderTop: idx ? "1px solid var(--border)" : "none" }}
                    >
                      <div className="col-span-6 sm:col-span-4 flex items-center gap-3 min-w-0">
                        <TokenIcon symbol={p.token} color={meta?.color || "#888"} size="sm" />
                        <div className="min-w-0">
                          <div className="font-display font-bold text-base flex items-center gap-2">
                            {p.token}
                            {isBoost && (
                              <span className="px-1.5 py-0.5 text-[9px] grad-boost-bg uppercase tracking-wider font-bold" style={{ color: "#fff" }}>
                                <Zap size={9} className="inline mr-0.5" /> Boost
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{p.chain} · {days}d ago</div>
                        </div>
                      </div>
                      <div className="hidden sm:block sm:col-span-2 font-mono text-sm tabular-nums">{fmtAmount(p.amount)}</div>
                      <div className="hidden sm:block sm:col-span-2 text-sm font-mono" style={{ color: isBoost ? "#FFB339" : "var(--text-2)" }}>{p.apr.toFixed(2)}%</div>
                      <div className="col-span-4 sm:col-span-2 text-right font-mono text-sm tabular-nums" style={{ color: "var(--success)" }}>+{fmtAmount(pending)}</div>
                      <div className="col-span-2 sm:col-span-2 text-right">
                        <button
                          onClick={() => window.location.href = `${p.chain}/index.html?symbol=${p.token}`}
                          data-testid={`manage-position-${p.id}`}
                          className="text-[10px] uppercase tracking-widest px-2 py-1 border lift"
                          style={{ borderColor: "var(--border-strong)" }}
                        >
                          Manage →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Balances */}
          <div className="mb-12">
            <SectionHeader icon={Wallet} label="Wallet balances" count={balanceTokenCount} />
            <div className="border" style={{ borderColor: "var(--border)" }} data-testid="balances-table">
              {ALL_TOKENS.filter((t) => (balances[`${t.chain}:${t.symbol}`] || 0) > 0).length === 0 ? (
                <div className="px-6 py-10 text-center text-sm" style={{ color: "var(--text-3)" }}>No balances yet.</div>
              ) : ALL_TOKENS.filter((t) => (balances[`${t.chain}:${t.symbol}`] || 0) > 0)
                .sort((a, b) => (balances[`${b.chain}:${b.symbol}`] || 0) - (balances[`${a.chain}:${a.symbol}`] || 0))
                .map((t, idx) => {
                  const bal = balances[`${t.chain}:${t.symbol}`] || 0;
                  return (
                    <div
                      key={`${t.chain}:${t.symbol}`}
                      data-testid={`balance-row-${t.symbol.toLowerCase()}`}
                      className="grid grid-cols-12 px-4 sm:px-6 py-3 items-center text-sm gap-3"
                      style={{ borderTop: idx ? "1px solid var(--border)" : "none" }}
                    >
                      <div className="col-span-6 sm:col-span-5 flex items-center gap-3 min-w-0">
                        <TokenIcon symbol={t.symbol} color={t.color} size="sm" />
                        <div className="min-w-0">
                          <div className="font-mono font-medium truncate">{t.symbol}</div>
                          <div className="text-[10px] uppercase tracking-wider truncate" style={{ color: "var(--text-3)" }}>{t.name}</div>
                        </div>
                      </div>
                      <div className="hidden sm:block sm:col-span-2 text-[10px] uppercase tracking-widest" style={{ color: t.chain === "evm" ? "var(--evm)" : "var(--sol)" }}>{t.chain}</div>
                      <div className="col-span-4 sm:col-span-3 text-right font-mono tabular-nums">{fmtAmount(bal)}</div>
                      <div className="col-span-2 sm:col-span-2 text-right">
                        <button
                          onClick={() => window.location.href = `${t.chain}/index.html?symbol=${t.symbol}`}
                          data-testid={`stake-action-${t.symbol.toLowerCase()}`}
                          className="text-[10px] uppercase tracking-widest px-2 py-1 border lift"
                          style={{ borderColor: "var(--border-strong)", color: "#fff" }}
                        >
                          Stake →
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* History */}
          <ProfileHistory />
        </section>
      )}

      {/* Wallet picker modal */}
      <WalletModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => setWallet(getWallet())}
        reason="Pick a wallet to manage your portfolio, history and active stakes."
      />
    </PageTransition>
  );
}

/* ──────────────────────────────────────────── */

function DisconnectedView({ onConnect }) {
  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20">
      <div className="absolute inset-0 -z-10 mesh-hero opacity-50" />
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ My Profile</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Sign in with your<br />
            <span className="grad-sol-text">wallet</span>.
          </h1>
          <p className="mt-5 max-w-xl text-sm sm:text-base" style={{ color: "var(--text-2)" }}>
            Self-custody. No email, no password — your wallet's signature is your identity.
            Pick a provider and you'll see balances, positions, claim history and 24h boost statistics in seconds.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onConnect}
              data-testid="profile-connect-wallet-btn"
              className="group inline-flex items-center gap-2 px-6 sm:px-7 h-12 grad-sol-bg btn-glow-sol font-bold tracking-wider text-sm uppercase relative overflow-hidden"
              style={{ color: "#fff" }}
            >
              <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.30) 50%, transparent 100%)", backgroundSize: "200% 100%", animation: "borderSweep 3.5s linear infinite" }} />
              <Wallet size={15} className="relative" />
              <span className="relative">Connect Wallet</span>
              <ArrowRight size={15} className="relative group-hover:translate-x-1 transition-transform" />
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 h-12 border text-xs uppercase tracking-wider lift"
              style={{ borderColor: "var(--border-strong)" }}
            >
              ← Back home
            </Link>
          </div>

          <div className="mt-10 flex items-center gap-2 text-xs" style={{ color: "var(--text-3)" }}>
            <ShieldCheck size={14} />
            Stakeforge never sees your private keys. All signatures happen inside your wallet app.
          </div>
        </div>

        {/* Right: feature card */}
        <div className="lg:col-span-5">
          <div className="border p-6 sm:p-7 relative overflow-hidden lift fade-up" style={{ borderColor: "var(--border-strong)", background: "var(--surface-1)" }}>
            <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full opacity-25 drift pointer-events-none" style={{ background: "var(--sol-grad)" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: "var(--text-3)" }}>What you unlock</div>
              <div className="font-display font-extrabold text-2xl tracking-tighter">Connected wallet · Live access</div>
              <ul className="mt-5 flex flex-col gap-3">
                <Feature icon={TrendingUp} text="Live pending rewards across every position" />
                <Feature icon={Zap}        text="Join 24h Boost pools with one click" />
                <Feature icon={Activity}   text="Full transaction history (stake / unstake / claim)" />
                <Feature icon={Clock}      text="Days-active counter and lifetime earned" />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <li className="flex items-center gap-3 text-sm" style={{ color: "var(--text-2)" }}>
      <span className="w-7 h-7 grid place-items-center border flex-shrink-0" style={{ borderColor: "var(--border)" }}>
        <Icon size={13} className="grad-sol-text" />
      </span>
      <span>{text}</span>
    </li>
  );
}

function SectionHeader({ icon: Icon, label, count }) {
  return (
    <div className="flex items-end justify-between mb-4 pb-3 border-b" style={{ borderColor: "var(--border)" }}>
      <div className="flex items-center gap-2">
        <Icon size={14} className="grad-sol-text" />
        <div className="font-display font-bold text-sm uppercase tracking-[0.2em]">{label}</div>
      </div>
      <span className="font-mono text-[10px]" style={{ color: "var(--text-3)" }}>{count}</span>
    </div>
  );
}

function Kpi({ label, valueNode, accent, hint }) {
  return (
    <div className="px-4 sm:px-5 py-5 sm:py-6" style={{ background: "var(--surface-1)" }}>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{label}</div>
      <div className="font-mono font-bold text-2xl sm:text-3xl mt-2 tabular-nums" style={{ color: accent || "#fff" }}>
        {valueNode}
      </div>
      {hint && <div className="text-[10px] mt-1" style={{ color: "var(--text-3)" }}>{hint}</div>}
    </div>
  );
}

function ProfileHistory() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setItems(getHistory());
    const i = setInterval(() => setItems(getHistory()), 2000);
    return () => clearInterval(i);
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <div data-testid="profile-history">
      <SectionHeader icon={Clock} label="Transaction log" count={items.length} />

      <div className="flex items-center gap-1 mb-4 overflow-x-auto">
        {[
          { id: "all",     label: "All" },
          { id: "stake",   label: "Stake" },
          { id: "unstake", label: "Unstake" },
          { id: "claim",   label: "Claim" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`profile-history-filter-${t.id}`}
            className="px-3 py-1 text-xs uppercase tracking-wider transition-colors"
            style={{
              background: filter === t.id ? "#fff" : "transparent",
              color:      filter === t.id ? "#000" : "var(--text-2)",
              border:     filter === t.id ? "1px solid #fff" : "1px solid var(--border)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }} data-testid="profile-history-table">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest border-b" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Asset</div>
          <div className="col-span-3 text-right">Amount</div>
          <div className="col-span-2">Pool</div>
          <div className="col-span-3 text-right">Time</div>
        </div>
        {filtered.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm" style={{ color: "var(--text-3)" }}>No transactions yet.</div>
        ) : filtered.slice(0, 50).map((tx, idx) => {
          const M = TX_META[tx.type] || TX_META.stake;
          const Icon = M.icon;
          return (
            <div
              key={tx.id}
              className="grid grid-cols-12 px-4 sm:px-6 py-3 items-center text-sm gap-3"
              style={{ borderTop: idx ? "1px solid var(--border)" : "none" }}
            >
              <div className="col-span-4 md:col-span-2 inline-flex items-center gap-2">
                <Icon size={13} style={{ color: M.color }} />
                <span style={{ color: M.color }}>{M.label}</span>
              </div>
              <div className="col-span-3 md:col-span-2 font-mono">{tx.token || "—"}</div>
              <div className="col-span-5 md:col-span-3 text-right font-mono tabular-nums">{tx.amount ? fmtAmount(tx.amount) : "—"}</div>
              <div className="hidden md:block md:col-span-2 text-[10px] uppercase tracking-widest" style={{ color: tx.pool === "boost" ? "#FFB339" : "var(--text-3)" }}>
                {tx.pool === "boost" ? "⚡ 24H BOOST" : "STANDARD"}
              </div>
              <div className="hidden md:block md:col-span-3 text-right text-xs font-mono" style={{ color: "var(--text-3)" }}>{new Date(tx.timestamp).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
